// Notes and recommendations section (step 5) for VisitReportPage.

import { useRef, type JSX } from "react";
import type { SubmitInput } from "@hcm/core";
import type { FormState, Action } from "../lib/visit-report-types.js";
import { SECTION, HEADING } from "../lib/visit-report-types.js";
import type { SectionAutoSaveBus } from "./VisitReportWizard.js";
import { FieldError, inputErrorClassName } from "./FieldError.js";
import { fieldErrorMessage, type SectionFieldErrors } from "../lib/visit-report-section-validators.js";
import { useFocusOnApiField } from "../hooks/use-focus-on-api-field.js";

// TASK-48 S2 — Typed against the canonical schema so a future rename
// of `supplementary_notes` / `carer_recommendation` will fail tsc here
// (this is a textarea + checkbox group, not a FormField/SelectField,
// so the typing is enforced via `keyof SubmitInput` consts rather than
// the FormField apiFieldName prop). Risk #4 6th-site fold-in.
const NOTES_API_FIELD:  keyof SubmitInput = "supplementary_notes";
const RECS_API_FIELD:   keyof SubmitInput = "carer_recommendation";

const REC_OPTIONS = [
  "GP review needed", "Specialist referral", "Medication review",
  "Physio referral", "Social worker referral", "Hospital admission",
];

interface NotesProps {
  f: FormState; dispatch: (a: Action) => void;
  bus?: SectionAutoSaveBus; fieldErrors?: SectionFieldErrors;
  /** Q9 S7 — when set, the named field is focused after this section renders. */
  focusFieldName?: string | null;
}

export function NotesSection({ f, dispatch, bus, fieldErrors, focusFieldName }: NotesProps): JSX.Element {
  const toggleRec = (opt: string): void => {
    const next = f.recommendations.includes(opt)
      ? f.recommendations.filter((r) => r !== opt)
      : [...f.recommendations, opt];
    dispatch({ field: "recommendations", value: next });
  };
  const notesError = fieldErrorMessage(fieldErrors, NOTES_API_FIELD);
  const recsError  = fieldErrorMessage(fieldErrors, RECS_API_FIELD);
  const containerRef = useRef<HTMLDivElement>(null);
  useFocusOnApiField(containerRef, focusFieldName);
  return (
    <div ref={containerRef} className={SECTION}>
      <p className={HEADING}>5 — Notes &amp; Recommendations</p>
      <div className="mb-3">
        <label htmlFor="supplementary-notes" className="block text-sm font-medium text-slate-700 mb-1">
          Supplementary notes
        </label>
        <textarea id="supplementary-notes" value={f.notes}
          onChange={(e) => dispatch({ field: "notes", value: e.target.value } as Action)}
          onBlur={bus?.onFieldBlur}
          maxLength={1000} rows={4}
          aria-invalid={!!notesError}
          data-api-field={NOTES_API_FIELD}
          className={`w-full rounded-lg border px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 resize-none ${inputErrorClassName(!!notesError)}`} />
        <p className="text-xs text-slate-400 text-right">{f.notes.length}/1000</p>
        <FieldError error={notesError} />
      </div>
      <p className="text-sm font-medium text-slate-700 mb-2">Carer recommendations</p>
      <div className="grid grid-cols-2 gap-1">
        {REC_OPTIONS.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.recommendations.includes(opt)} onChange={() => toggleRec(opt)}
              className="rounded border-slate-300 text-navy-700 focus:ring-navy-500" />
            {opt}
          </label>
        ))}
      </div>
      <FieldError error={recsError} />
    </div>
  );
}
